import { useState, useEffect, useRef, useCallback } from 'react'
import { ETLProgressUpdate } from 'types'

interface UseETLWebSocketOptions {
  organizationId: number
  token: string
  enabled?: boolean
}

interface WebSocketState {
  progress: ETLProgressUpdate | null
  connected: boolean
  error: string | null
}

const useETLWebSocket = ({
  organizationId,
  token,
  enabled = true,
}: UseETLWebSocketOptions) => {
  const [state, setState] = useState<WebSocketState>({
    progress: null,
    connected: false,
    error: null,
  })

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5
  const heartbeatInterval = 30000 // 30 seconds

  const getWebSocketUrl = useCallback(() => {
    if (!token) {
      console.error('WebSocket: No token provided')
      return null
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    let host = window.location.host

    // For local development
    if (host.includes('localhost') || host.includes('127.0.0.1') || host.includes('local.mayainsights.com')) {
      host = 'localhost:8000'
    } else {
      // Production/Staging: use api subdomain
      // app.mayainsights.com -> api.mayainsights.com
      // maya-staging-frontend.mayabackup.com -> maya-staging-api.mayabackup.com
      if (host.includes('app.mayainsights.com')) {
        host = host.replace('app.mayainsights.com', 'api.mayainsights.com')
      } else if (host.includes('frontend')) {
        host = host.replace('frontend', 'api')
      } else {
        // Fallback: replace port if present
        host = host.replace(':3000', ':8000')
      }
    }

    console.log('WebSocket: Using host:', host, 'from window.location.host:', window.location.host)
    return `${protocol}//${host}/notifications/?token=${encodeURIComponent(token)}`
  }, [token])

  const connect = useCallback(() => {
    if (!enabled || !organizationId || !token) {
      console.log('WebSocket: Not connecting (enabled:', enabled, 'orgId:', organizationId, 'hasToken:', !!token, ')')
      return
    }

    try {
      const wsUrl = getWebSocketUrl()
      if (!wsUrl) {
        setState((prev) => ({
          ...prev,
          error: 'Failed to construct WebSocket URL',
        }))
        return
      }

      console.log('WebSocket: Connecting to:', wsUrl.replace(token, 'TOKEN_REDACTED'))

      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('WebSocket: Connected successfully')
        setState((prev) => ({ ...prev, connected: true, error: null }))
        reconnectAttemptsRef.current = 0

        // Start heartbeat to keep connection alive
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
        }
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            console.log('WebSocket: Sending ping')
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, heartbeatInterval)
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)

          // Filter for ETL progress updates for this organization
          if (
            message.event === 'etl_progress_updated' &&
            message.organization_id === organizationId
          ) {
            console.log('ETL progress update received:', message.data)

            // Flatten the nested progress structure for the frontend
            const flattenedProgress: ETLProgressUpdate = {
              running: message.data.running,
              current_step: message.data.current_step,
              points: message.data.progress.points,
              description: message.data.progress.description,
              step_started_at: message.data.progress.step_started_at,
              errors: message.data.progress.errors || [],
            }

            console.log('Flattened progress:', flattenedProgress)

            setState((prev) => ({
              ...prev,
              progress: flattenedProgress,
            }))
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket: Error occurred', error)
        console.error('WebSocket: This may indicate that WebSocket is not available at:', wsUrl?.replace(token, 'TOKEN_REDACTED'))
        console.error('WebSocket: Check that the WebSocket server is running and accessible')
        setState((prev) => ({
          ...prev,
          error: 'WebSocket connection error - server may be unavailable',
        }))
      }

      ws.onclose = (event) => {
        console.log('WebSocket: Connection closed (code:', event.code, 'reason:', event.reason || 'none', ')')
        setState((prev) => ({ ...prev, connected: false }))

        // Clear heartbeat interval
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
          heartbeatIntervalRef.current = null
        }

        // Attempt to reconnect if not manually closed and under max attempts
        if (
          event.code !== 1000 &&
          reconnectAttemptsRef.current < maxReconnectAttempts &&
          enabled
        ) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`)

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1
            connect()
          }, delay)
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setState((prev) => ({
            ...prev,
            error: 'Max reconnection attempts reached',
          }))
        }
      }
    } catch (error) {
      console.error('Error creating WebSocket:', error)
      setState((prev) => ({
        ...prev,
        error: 'Failed to create WebSocket connection',
      }))
    }
  }, [enabled, organizationId, token, getWebSocketUrl])

  const disconnect = useCallback(() => {
    console.log('WebSocket: Disconnecting')

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }

    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close(1000, 'Component unmounted')
      wsRef.current = null
    }
  }, [])

  useEffect(() => {
    if (enabled) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [enabled, connect, disconnect])

  return {
    progress: state.progress,
    connected: state.connected,
    error: state.error,
    reconnect: connect,
  }
}

export default useETLWebSocket
